// @ts-nocheck
/**
 * GeoTIFF Playground - Test Viewer
 * Uses production GeoTiffViewerEmbedded component with backend API data
 */

'use client';

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Layers, Map, MapPin, Sun, Loader2, X, Trash2 } from 'lucide-react';
import GeoTiffViewerEmbedded from './GeoTiffViewerEmbedded';
import PlacesSearch from '../components/map/PlacesSearch';
import RPUSearchBar from '../components/map/RPUSearchBar';
import CacheStatusBadge from '../components/map/CacheStatusBadge';

import { env } from 'env';
// API configuration
const API_URL = env.API_URL || 'http://localhost:5001';

export default function GeoTiffViewer() {
  const [controlsOpen, setControlsOpen] = useState(true);
  const [selectedLayer, setSelectedLayer] = useState<'rgb' | 'dsm' | 'flux'>('rgb');
  const [showMask, setShowMask] = useState(false);
  const [mapMode, setMapMode] = useState<'geotiff-only' | 'leaflet-satellite'>('leaflet-satellite');

  // API data state
  const [solarData, setSolarData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Location state
  const [pinPosition, setPinPosition] = useState(null);
  const [lastFetchedPosition, setLastFetchedPosition] = useState(null);
  const [selectedRPU, setSelectedRPU] = useState(null);

  // Cache controls
  const [cacheRadius, setCacheRadius] = useState(50);
  const [forceFetch, setForceFetch] = useState(false);
  const [cacheControlsExpanded, setCacheControlsExpanded] = useState(true);

  const layers = [
    { id: 'rgb', name: 'RGB', icon: '🖼️' },
    { id: 'dsm', name: 'DSM (Height)', icon: '🏔️' },
    { id: 'flux', name: 'Solar Flux', icon: '☀️' },
  ];

  // Fetch solar data from backend API
  const fetchSolarData = async (location: { lat: number; lng: number }) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/solar/process-geotiff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lat: location.lat,
          lng: location.lng,
          radius_m: cacheRadius,
          force: forceFetch,
          simplify: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}: ${await response.text()}`);
      }

      const data = await response.json();

      // Transform to match GeoTiffViewerEmbedded expected format
      const transformedData = {
        buildingInsights: {
          center: data.center,
          imageryDate: data.imagery_date,
          imageryProcessedDate: data.imagery_date,
          roofSegmentStats: [],
          solarPotential: {
            maxArrayPanelsCount: 100,
            maxArrayAreaMeters2: 0,
          },
        },
        dataLayers: {
          imageryQuality: 'HIGH',
          rgbUrl: data.geotiff_urls?.rgb || '',
          maskUrl: data.geotiff_urls?.mask || '',
          dsmUrl: data.geotiff_urls?.dsm || '',
          annualFluxUrl: data.geotiff_urls?.flux || '',
        },
        cacheInfo: {
          geotiffCacheId: data.cache_id,
          cacheStatus: data.cache_hit ? 'HIT' : 'MISS',
          cacheHit: data.cache_hit,
        },
      };

      setSolarData(transformedData);
      setLastFetchedPosition(location);
      setLoading(false);
    } catch (err) {
      console.error('❌ Failed to fetch solar data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Handle place selection from search
  const handlePlaceSelected = async (place: { lat: number; lng: number; address: string }) => {
    const newPosition = { lat: place.lat, lng: place.lng };
    setPinPosition(newPosition);
    setSelectedRPU(null);
    await fetchSolarData(newPosition);
  };

  // Handle RPU selection from search
  const handleSelectRPU = async (result: { rpu: string; address: string; latitude: number; longitude: number }) => {
    setSelectedRPU(result);
    const newPosition = { lat: result.latitude, lng: result.longitude };
    setPinPosition(newPosition);
    await fetchSolarData(newPosition);
  };

  // Handle Get Solar Data button
  const handleGetSolarData = async () => {
    if (!pinPosition) return;
    await fetchSolarData(pinPosition);
  };

  // Clear location
  const clearLocation = () => {
    setSolarData(null);
    setPinPosition(null);
    setLastFetchedPosition(null);
    setSelectedRPU(null);
    setError(null);
  };

  // Clear cache
  const handleClearCache = async () => {
    const currentCacheId = solarData?.cacheInfo?.geotiffCacheId;

    if (!currentCacheId) {
      alert('⚠️ No cache ID found for this session');
      return;
    }

    if (!window.confirm(`⚠️ Clear cache for this location?\n\nCache ID: ${currentCacheId}\n\nThis will delete the cached data.`)) {
      return;
    }

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/geotiff/cache/${currentCacheId}`, {
        method: 'DELETE',
        headers: headers,
      });

      const result = await response.json();

      if (result.success) {
        alert(`✅ Successfully cleared cache for ${currentCacheId}!`);
        clearLocation();
      } else {
        alert(`❌ Failed to clear cache: ${result.error}`);
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      alert(`❌ Error clearing cache: ${error}`);
    }
  };

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Left Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 border-b border-yellow-600">
          <h1 className="text-xl font-bold text-gray-900">GeoTIFF Playground</h1>
          <p className="text-sm text-gray-800 mt-1">Production Renderer Test Mode</p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Search Section */}
          <div className="space-y-3 pb-4 border-b-2 border-gray-200">
            <div>
              <h3 className="text-xs font-bold text-gray-900 mb-2 uppercase">Search by RPU</h3>
              <RPUSearchBar onSelectRPU={handleSelectRPU} />
            </div>
            <div>
              <h3 className="text-xs font-bold text-gray-900 mb-2 uppercase">Search by Address</h3>
              <PlacesSearch
                onPlaceSelected={handlePlaceSelected}
                disabled={loading}
              />
            </div>
          </div>

          {/* Location Info */}
          {pinPosition && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-green-800 mb-2">
                <MapPin className="h-4 w-4" />
                <span className="text-sm font-semibold">Current Location</span>
              </div>
              <div className="text-xs text-green-700 space-y-1">
                <div><span className="font-medium">Lat:</span> {pinPosition.lat.toFixed(6)}</div>
                <div><span className="font-medium">Lng:</span> {pinPosition.lng.toFixed(6)}</div>
                {selectedRPU && (
                  <div className="mt-2 pt-2 border-t border-green-200">
                    <div><span className="font-medium">RPU:</span> {selectedRPU.rpu}</div>
                    {selectedRPU.address && (
                      <div className="text-[10px] mt-1">{selectedRPU.address}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cache Controls */}
          <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setCacheControlsExpanded(!cacheControlsExpanded)}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
            >
              <span className="text-xs font-semibold text-gray-700">Cache Controls</span>
              {cacheControlsExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {cacheControlsExpanded && (
              <div className="p-4 pt-0 space-y-3 border-t border-gray-200">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-gray-700">Cache Radius: {cacheRadius}m</label>
                    <span className="text-[10px] text-gray-500">
                      {cacheRadius <= 50 ? 'Tight' : cacheRadius <= 100 ? 'Balanced' : cacheRadius <= 150 ? 'Moderate' : 'Loose'}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="25"
                    max="250"
                    step="25"
                    value={cacheRadius}
                    onChange={(e) => setCacheRadius(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <label className="flex items-center justify-between gap-2 cursor-pointer px-3 py-2 bg-gray-50 border rounded-lg hover:bg-gray-100">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={forceFetch}
                      onChange={(e) => setForceFetch(e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Force Refresh</span>
                  </div>
                </label>

                {/* Clear Cache Button */}
                {solarData?.cacheInfo && (
                  <button
                    onClick={handleClearCache}
                    className="w-full px-4 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-semibold shadow-sm border-2 border-red-300 flex items-center justify-center gap-2"
                    title={`Clear cache (${solarData.cacheInfo.geotiffCacheId})`}
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="text-sm">Clear Cache</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Primary Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleGetSolarData}
              disabled={!pinPosition || loading}
              className="flex-1 flex items-center justify-center px-4 py-3 bg-[#EBFD03] text-black rounded-lg hover:bg-yellow-400 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  <span className="text-xs">Loading...</span>
                </div>
              ) : solarData ? (
                <div className="flex items-center">
                  <Sun className="h-5 w-5 mr-2" />
                  <span className="text-sm">Re-fetch</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <Sun className="h-5 w-5 mr-2" />
                  <span className="text-sm">Get Solar</span>
                </div>
              )}
            </button>
            {solarData && (
              <button
                onClick={clearLocation}
                className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
                title="Clear location and reset"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Cache Status Badge */}
          <CacheStatusBadge
            cacheInfo={solarData?.cacheInfo}
            isLoading={loading}
            dataQuality={solarData?.dataLayers?.imageryQuality}
            solarAvailable={!!solarData}
          />

          {/* Solar Layers */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setControlsOpen(!controlsOpen)}
              className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-yellow-600" />
                <span className="font-semibold text-gray-900">Solar Layers</span>
              </div>
              {controlsOpen ? <ChevronUp className="h-5 w-5 text-gray-600" /> : <ChevronDown className="h-5 w-5 text-gray-600" />}
            </button>

            {controlsOpen && (
              <div className="p-4 space-y-4 border-t border-gray-200">
                {/* Layer Selection */}
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Base Layer</div>
                  <div className="space-y-1">
                    {layers.map((layer) => (
                      <button
                        key={layer.id}
                        onClick={() => setSelectedLayer(layer.id as any)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedLayer === layer.id
                            ? 'bg-yellow-100 text-yellow-900 font-semibold border border-yellow-300'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <span className="mr-2">{layer.icon}</span>
                        {layer.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mask Toggle */}
                <div className="pt-3 border-t border-gray-200">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showMask}
                      onChange={(e) => setShowMask(e.target.checked)}
                      className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                    />
                    <span className="text-sm text-gray-700">Show Roof Mask</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Map Mode */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="p-4 space-y-3">
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Map className="h-4 w-4 text-yellow-600" />
                  Map Mode
                </div>
                <div className="space-y-1">
                  {[
                    { value: 'geotiff-only', label: 'Photo Only', icon: '📷' },
                    { value: 'leaflet-satellite', label: 'Satellite + Photo', icon: '🛰️' },
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      onClick={() => setMapMode(mode.value as any)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        mapMode === mode.value
                          ? 'bg-yellow-100 text-yellow-900 font-semibold border border-yellow-300'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <span className="mr-2">{mode.icon}</span>
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="text-xs font-medium text-gray-600 mb-2">System Status</div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">API:</span>
                <span className="text-green-600 font-medium">connected</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Renderer:</span>
                <span className="text-blue-600 font-medium">Production</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Mode:</span>
                <span className="text-purple-600 font-medium">Playground</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Map - Using Production GeoTiffViewerEmbedded */}
      <div className="flex-1 relative">
        {loading && (
          <div className="flex items-center justify-center h-full bg-gray-100">
            <div className="text-center">
              <Loader2 className="h-12 w-12 text-yellow-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 font-semibold">Fetching solar data...</p>
            </div>
          </div>
        )}
        {error && !loading && (
          <div className="flex items-center justify-center h-full bg-gray-100">
            <div className="text-center">
              <p className="text-red-600 font-semibold mb-2">Failed to load solar data</p>
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          </div>
        )}
        {!loading && !error && solarData?.buildingInsights && solarData?.dataLayers && pinPosition ? (
          <GeoTiffViewerEmbedded
            buildingInsights={solarData.buildingInsights}
            dataLayers={solarData.dataLayers}
            cacheId={solarData.cacheInfo?.geotiffCacheId || 'playground'}
            pinPosition={pinPosition}
            selectedLayer={selectedLayer}
            showMask={showMask}
            mapMode={mapMode}
            showPinMarker={true}
            compactMode={false}
          />
        ) : !loading && !error && (
          <div className="flex items-center justify-center h-full bg-gray-100">
            <div className="text-center">
              <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-semibold mb-2">Search for a location to begin</p>
              <p className="text-sm text-gray-500">Use the search bars on the left to find an address or RPU</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
